import config from 'constants/config'
import bookingProtectionGlossaryMessages from 'i18n/bookingProtectionGlossaryMessages'
import productLabels from 'i18n/productLabels'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { capitalise } from 'lib/string/stringUtils'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

/** Shared with designation labels in `checkout/utils` so EN/DE stay in sync. */
export const accommodationDesignationMessages = defineMessages({
  accommodation: {
    id: 'zdmuwpJMsikmxWcwof6n',
    defaultMessage: 'accommodation',
    description: 'Generic accommodation designation label used in checkout data and rendered as a heading in review sections',
  },
  preAccommodation: {
    id: 'NnZL1OXra6V4ITrsVqOF',
    defaultMessage: 'pre accommodation',
    description: 'Checkout designation label for accommodation booked before a tour or cruise and rendered in review sections',
  },
  postAccommodation: {
    id: 's2dGUEKVliNUQz3K9hQn',
    defaultMessage: 'post accommodation',
    description: 'Checkout designation label for accommodation booked after a tour or cruise and rendered in review sections',
  },
})

/**
 * Display messages for known checkout price-breakdown section titles.
 * Producers set `{ kind: 'known', key }`; only the UI calls `resolvePriceBreakdownTitle`.
 */
export const priceBreakdownTitleMessages = defineMessages({
  accommodation: {
    id: '5YTw3yvipPddoj1taqU6',
    defaultMessage: 'Accommodation',
    description: 'Checkout breakdown title for hotel and accommodation offers',
  },
  hotelPlusFlightsPackage: {
    id: 'yce-MyTLUF_tXbR7VvEg',
    defaultMessage: 'Hotel + Flights Package',
    description: 'Title for bundled hotel and flights package in checkout breakdown',
  },
  optionalExperiences: {
    id: 'UUZ27jW_FPEmtCN490UQ',
    defaultMessage: 'Optional experiences',
    description: 'Breakdown title for optional tour experiences in checkout summary',
  },
  transfers: {
    id: 'KAJt4877eOhLAGk60KY5',
    defaultMessage: 'Transfers',
    description: 'Breakdown title for transfers in the checkout summary',
  },
  giftCard: {
    id: 'meLG8DcbHewQZq9s6nav',
    defaultMessage: 'Gift Card',
    description: 'Breakdown title for the gift card in the checkout summary',
  },
  purchaseCredit: {
    id: 'NdWpBQNgt0VVYkeNYIxm',
    defaultMessage: 'Purchase Credit',
    description: 'Heading shown in the checkout price breakdown for standalone purchase credit items',
  },
  travelEsim: {
    id: 'BT6gFIM2VjNiDHJUTt93',
    defaultMessage: 'Travel eSIM',
    description: 'Title for travel eSIM breakdown view in checkout summary',
  },
  additionalDataRoaming: {
    id: 'VvoeeuxWV-e2k_0PA7RL',
    defaultMessage: 'Additional Data Roaming',
    description: 'Section title for paid eSIM upsell items in checkout breakdown',
  },
  airportLoungePass: {
    id: 'V-0Kx6DXhHrcvnLImsJZ',
    defaultMessage: 'Airport lounge pass',
    description: 'Breakdown title for airport lounge pass in checkout summary',
  },
  luxPlusMembership: {
    id: '7ClYlwOBXpLIELEWdLdZ',
    defaultMessage: '{programName} Membership',
    description: 'Title for LuxPlus membership in checkout price breakdown',
  },
  travelInsurance: {
    id: 'ObqrvteA_97C9Pe3iu3T',
    defaultMessage: 'Travel {insuranceProductName}',
    description: 'Breakdown title for travel insurance/protection in checkout summary',
  },
  cancellationInsurance: {
    id: 'zAVIfqIX9uPiHBW4t_W8',
    defaultMessage: 'Cancellation {insuranceProductName}',
    description: 'Breakdown title for cancellation insurance/protection in checkout summary',
  },
  travelInsuranceFixed: {
    id: 'mF2rZmNfE-irHifMnZ1o',
    defaultMessage: 'Travel Insurance',
    description: 'Title for PassportCard travel insurance in checkout breakdown',
  },
  pointsToPurchase: {
    id: '_lsD5QgEijPJpp50pjgW',
    defaultMessage: 'Points to purchase',
    description: 'Breakdown title for the buy-points entry in the checkout right-column price summary',
  },
  flightClassUpgrade: {
    id: 'XsIxMeeHcllZiON4Mudg',
    defaultMessage: 'Flight class upgrade',
    description: 'Charter upgrade section title in the checkout price breakdown',
  },
  airlineChangeFee: {
    id: 'wX_Ni_5p2LEqzAftTCIS',
    defaultMessage: 'Airline change fee',
    description: 'Breakdown title for airline change fee in checkout summary',
  },
  brandChangeFee: {
    id: 'CVMEccdqXijncKzMXivH',
    defaultMessage: '{brandName} change fee',
    description: 'Title for brand-specific change fee in flight date change breakdown',
  },
  newFlightAdditionalCost: {
    id: 'Fep10ixzHW-mF9zRMMnz',
    defaultMessage: 'New flight additional cost',
    description: 'Breakdown title for additional cost of new flights after date change',
  },
  package: {
    id: 'tnkE1tpiUk0lj-AQWi7j',
    defaultMessage: 'Package',
    description: 'Checkout breakdown title for generic package offers',
  },
  carHireInsurance: {
    id: 'tOzW8VtMLXqn26Ehrwky',
    defaultMessage: 'Insurance',
    description: 'Section title for car hire insurance price breakdown',
  },
  carHireAddons: {
    id: '1zYNmZLXaJBPN6v6EuVS',
    defaultMessage: 'Add-ons',
    description: 'Section title for car hire add-ons price breakdown',
  },
  additionalCharges: {
    id: '0DSJ6BDaG7bj7xtxUnvi',
    defaultMessage: 'Additional charges',
    description: 'Generic fallback breakdown title when the checkout title is not mapped to a specific localized message',
  },
  hotelPlusCoPayment: {
    id: '4n2vqP65XzDKjKxRGr9v',
    defaultMessage: 'Hotel + Co-Payment',
    description: 'Breakdown section title in the checkout summary for the bundle that contains a Classic Rewards flight, indicating the section total covers the hotel and the cash co-pay portion of the Classic Rewards flight (the points portion is shown separately on the total row).',
  },
})

const DESIGNATION_TITLE_KEYS = new Set<App.Checkout.PriceBreakdownTitleKey>([
  'preAccommodation',
  'postAccommodation',
])

const KNOWN_TITLE_DESCRIPTORS: Record<App.Checkout.PriceBreakdownTitleKey, MessageDescriptor> = {
  accommodation: priceBreakdownTitleMessages.accommodation,
  preAccommodation: accommodationDesignationMessages.preAccommodation,
  postAccommodation: accommodationDesignationMessages.postAccommodation,
  hotelPlusFlightsPackage: priceBreakdownTitleMessages.hotelPlusFlightsPackage,
  experiences: productLabels.experiences,
  optionalExperiences: priceBreakdownTitleMessages.optionalExperiences,
  transfers: priceBreakdownTitleMessages.transfers,
  giftCard: priceBreakdownTitleMessages.giftCard,
  purchaseCredit: priceBreakdownTitleMessages.purchaseCredit,
  travelEsim: priceBreakdownTitleMessages.travelEsim,
  additionalDataRoaming: priceBreakdownTitleMessages.additionalDataRoaming,
  airportLoungePass: priceBreakdownTitleMessages.airportLoungePass,
  luxPlusMembership: priceBreakdownTitleMessages.luxPlusMembership,
  cancellationProtection: bookingProtectionGlossaryMessages.cancellationProtection,
  luxuryEscapesProtect: bookingProtectionGlossaryMessages.luxuryEscapesProtect,
  travelProtection: bookingProtectionGlossaryMessages.travelProtection,
  travelInsurance: priceBreakdownTitleMessages.travelInsurance,
  cancellationInsurance: priceBreakdownTitleMessages.cancellationInsurance,
  travelInsuranceFixed: priceBreakdownTitleMessages.travelInsuranceFixed,
  pointsToPurchase: priceBreakdownTitleMessages.pointsToPurchase,
  flightClassUpgrade: priceBreakdownTitleMessages.flightClassUpgrade,
  airlineChangeFee: priceBreakdownTitleMessages.airlineChangeFee,
  brandChangeFee: priceBreakdownTitleMessages.brandChangeFee,
  newFlightAdditionalCost: priceBreakdownTitleMessages.newFlightAdditionalCost,
  tour: productLabels.tour,
  cruise: productLabels.cruise,
  package: priceBreakdownTitleMessages.package,
  carHireInsurance: priceBreakdownTitleMessages.carHireInsurance,
  carHireAddons: priceBreakdownTitleMessages.carHireAddons,
  additionalCharges: priceBreakdownTitleMessages.additionalCharges,
}

function getDefaultTitleValues(
  key: App.Checkout.PriceBreakdownTitleKey,
): Record<string, string | number> | undefined {
  switch (key) {
    case 'luxPlusMembership':
      return { programName: LUXURY_PLUS.PROGRAM_NAME }
    case 'brandChangeFee':
      return { brandName: config.brandConfig.name }
    default:
      return undefined
  }
}

export function knownPriceBreakdownTitle(
  key: App.Checkout.PriceBreakdownTitleKey,
  values?: Record<string, string | number>,
): App.Checkout.PriceBreakdownTitle {
  return values ? { kind: 'known', key, values } : { kind: 'known', key }
}

export function customPriceBreakdownTitle(text: string): App.Checkout.PriceBreakdownTitle {
  return { kind: 'custom', text }
}

export function resolvePriceBreakdownTitle(
  title: App.Checkout.PriceBreakdownTitle,
  intl: IntlShape,
): string {
  if (title.kind === 'custom') {
    return title.text
  }

  const resolved = intl.formatMessage(
    KNOWN_TITLE_DESCRIPTORS[title.key],
    { ...getDefaultTitleValues(title.key), ...title.values },
  )

  // Designation labels are lowercase in EN; section headings capitalise the first letter.
  return DESIGNATION_TITLE_KEYS.has(title.key) ? capitalise(resolved) : resolved
}
